/** Sources: what we expect to receive, and what has not arrived.
 *
 * The screen behind one sentence from the enterprise review — *unknown is not zero*. Every total in
 * this product is a sum over the activity that arrived, and a sum over what arrived looks exactly
 * like a complete inventory. A site with a standby generator and no fuel invoices reports zero
 * mobile combustion; the arithmetic is right and the claim is false.
 *
 * Three decisions about what it shows.
 *
 * **Six states, six words.** `submitted`, `declared nil`, `missing`, `pending`, `unknown` and
 * `not expected` each carry a label, not only a colour. A reader who cannot separate two hues still
 * has to tell a gap from a declared zero — and a screenshot in a review pack arrives without the
 * legend. ADR-0007's rule about colour is about tokens; this is the other half of it.
 *
 * **The gap is the headline.** The summary leads with what is missing rather than with a
 * completeness percentage, because a percentage invites rounding up: "94% complete" reads as a pass
 * and "3 windows missing" reads as three things to chase. The second one is actionable and the first
 * one is a grade.
 *
 * **Declaring nil happens next to the gap it answers.** The form opens under the window a reviewer
 * clicked, pre-filled with that window's dates, and the reason is required — an assurance provider's
 * first question about a declared zero is who said so and why, and a form that made the reason
 * optional would be collecting the answer to neither.
 *
 * **And taking one back happens in the same place** (ADR-0064). A declared nil is a person's
 * statement, so it is corrected by being withdrawn, never edited: the cell that says *nil* opens a
 * form that shows what was said and asks *why that was wrong* — a different sentence, and required.
 * The period then goes back to whatever the census computes, which is usually *missing* because that
 * is what it now is. It does not become a zero.
 *
 * **Who bills the meter is said on the row** (ADR-0062), in words: *billed by*, or *not recorded*,
 * or *not tied to a meter*. Never "no supplier" — a meter nobody has attached to an account is a
 * fact about what somebody has told the product, and every meter that predates the register is in
 * that state.
 */

import { useEffect, useState } from "react";

import { useCoverageRollup } from "../api/coverageRollup";
import type { SourceCoverage, SourceWindow } from "../api/sources";
import type { ExpectedSourceIn } from "../api/sources";
import {
  useAssignOwner,
  useCoverage,
  useDeclareExpected,
  useDeclareNil,
  useNilTrail,
  useSourceLinks,
  useStreamOptions,
  useWithdrawNil,
} from "../api/sources";
import { useMeters } from "../api/provisioning";
import { useMe } from "../api/auth";
import type { Member } from "../api/queries";
import { useMembers } from "../api/queries";
import { Loaded, ReadFailed, WriteFailed } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Empty, Panel } from "../components/Panel";
import { StatusPill } from "../components/StatusPill";
import { shortDate, statedQuantity, unitLabel } from "../lib/format";
import { RequireSession } from "../shell/RequireSession";
import { IngestionPanel } from "./IngestionPanel";
import { RecordLink } from "./RecordLink";
import { SourceRetirement } from "./SourceRetirement";
import {
  LIFE_BASIS,
  MONTHS,
  STATE,
  SourcesNavContext,
  jumpTo,
  nameOf,
  period,
} from "./sourceModel";
import { StateGlyph, SupplyLine } from "./sourceParts";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Evidence.module.css";
import own from "./Sources.module.css";
import reg from "./Register.module.css";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";

import { isTabularContentType } from "../lib/documents";
import { A_COLLEAGUE, useNameOf } from "./PersonRef";
import { keepSearch } from "./search";
import { narrowCoverage } from "./sourceNarrowing";
import { useFacilities } from "../api/evidence";
import { isPeriodClosed } from "../api/refusal";
import { RequirePeriod } from "./NoPeriod";
import { PostCloseRetry } from "./PeriodLock";
import { SourceRegisters } from "./SourcesRegisters";
import { SourcesOverview, SourcesSiteCoverage } from "./SourcesCoverage";
import { SourcesHeatmap } from "./SourcesHeatmap";
import { CATEGORY_LABEL } from "../lib/categoryLabel";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import cov from "./SourcesCoverage.module.css";
import type { Screen, Window as ScreenWindow } from "./useScreen";

/** How many streams the register draws before the reader asks for more. Each row carries its own
 * owner control, twelve period cells and three actions, so the cost of a row is not the cost of a
 * line of text. */
const SOURCE_CEILING = 40;

/** The three views of the screen, in the URL as `tab` so a view is a link and absent means the
 * first. **Sources** is the work (the register, with the forms); **Coverage** is the picture (the
 * grid and where the gaps are); **Who bills what** is the supply register (ADR-0062). */
const TABS = [
  { key: "sources", label: "Sources" },
  { key: "coverage", label: "Coverage" },
  { key: "billing", label: "Who bills what" },
] as const;
type TabKey = (typeof TABS)[number]["key"];
const isTab = (value: unknown): value is TabKey => TABS.some((entry) => entry.key === value);

function SourcesScreenBody({ screen, current }: { screen: Screen; current: ScreenWindow }) {
  const {
    data,
    isPending,
    error,
    refetch: retryCensus,
  } = useCoverage(current.period_start, current.period_end);
  const [declaring, setDeclaring] = useState<{ source: SourceCoverage; window: SourceWindow } | null>(
    null,
  );
  const [reason, setReason] = useState("");
  /** The declared nil an Approver is taking back (ADR-0064), and the reason they are giving. Its
   * own state beside `declaring` and not a mode of it: the two forms ask different questions of
   * different cells, and one slot would let a typed *"why is there no gas"* sit in the box that
   * is asking *"why was that wrong"*. */
  const [withdrawing, setWithdrawing] = useState<{
    source: SourceCoverage;
    window: SourceWindow;
  } | null>(null);
  const [withdrawReason, setWithdrawReason] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);
  /** Which streams the register lists, and how many of them. **The ceiling is the reader's**: a
   * workspace with three hundred streams was one scroll of three hundred rows, and the answer to
   * that is a filter and a count that says what is not on screen - never a silent cut. */
  const [show, setShow] = useState<"all" | "gaps" | "unowned">("all");
  const [ceiling, setCeiling] = useState(SOURCE_CEILING);
  /** **Two different things, and they were one `string | null`.** `message` is the *success*
   * sentence a newly declared expectation produces; a refusal is why a request did not complete,
   * which has its own remedies (EXP-29's second list). One slot meant a stale success sentence
   * could sit where a refusal belonged, and a 409 read as prose about a declaration. */
  const [message, setMessage] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<unknown>(null);
  const declare = useDeclareNil(current.period_start, current.period_end);
  const withdraw = useWithdrawNil(current.period_start, current.period_end);
  const { data: me } = useMe();
  // Declaring that nothing happened is an assertion a filing rests on, not data entry — the server
  // requires an Approver (ADR-0008). Reflected here so a Contributor sees a gap they cannot close
  // and is told who can, rather than clicking a cell and collecting a 403.
  const mayDeclare = me?.role === "approver" || me?.role === "admin";
  /** **Naming the operating owner is the same tuple** — `MAY_DECLARE` on `api/sources.py` gates
   * `POST /sources/{id}/owner`, `POST /sources/{id}/nil` and `POST /sources` alike. Its own name
   * rather than a reuse of `mayDeclare` so the day one of them moves, exactly one of these lines
   * changes; aliased rather than re-derived so they cannot drift while the tuple is one. */
  const mayAssignOwner = mayDeclare;
  /** **Stopping a stream is the same tuple again** — `POST /sources/{id}/retire` is `MAY_DECLARE`.
   * `undefined` while the session loads, so the control is withheld rather than the sentence
   * flashed at somebody who may well be allowed (F-006). */
  const mayRetire = me ? mayDeclare : undefined;
  const isAdmin = me?.role === "admin";
  /** The workspace roster, **and it is Admin-only on the server** (`MAY_ADMINISTER` on
   * `api/org.py`, by a deliberate separation-of-duties argument: an Approver approves figures and
   * does not decide who else may).
   *
   * So the role that may *assign* an owner is wider than the role that may read the list of people
   * to assign one from, and the screen has to say so rather than render a picker that resolves to
   * nothing. `OwnerControls` below is that fork. If `/members` is ever widened to `MAY_DECLARE`,
   * `mayPickOwner` becomes `mayAssignOwner` and the fork collapses — nothing else changes.
   *
   * **Loaded on page open, not when a picker opens**, which is what the `enabled` flag is deciding
   * here. The roster resolves the owner's *name* on every owned row, not only inside the control,
   * so deferring it would print "a colleague" against every source until somebody opened a picker
   * they may not have wanted to open. It is one request for the whole screen either way, and the
   * flag still does real work: a Contributor's page load never fires it, so it never collects a
   * 403 that `retry: false` would merely stop repeating.
   */
  const roster = useMembers(isAdmin);
  // **Not `?? []`.** A failed roster read must not render as an empty picker — the same defect the
  // site list in `DeclareExpected` below had, where a dropped request became a form that submitted
  // the empty string. `undefined` here means *we did not get the list*, and the control degrades
  // to the one that needs no list instead of offering a choice of nobody.
  const people = roster.data;
  const mayPickOwner = Boolean(mayAssignOwner && isAdmin && !roster.error && people);

  async function onDeclare() {
    if (!declaring) return;
    setRefusal(null);
    setMessage(null);
    try {
      await declare.mutateAsync({
        sourceId: declaring.source.source_id,
        period_start: declaring.window.period_start,
        period_end: declaring.window.period_end,
        reason,
      });
      setDeclaring(null);
      setReason("");
    } catch (err) {
      setRefusal(err);
    }
  }

  async function onWithdraw() {
    if (!withdrawing) return;
    const declarationId = withdrawing.window.nil_declaration_id;
    if (!declarationId) return;
    setRefusal(null);
    setMessage(null);
    try {
      await withdraw.mutateAsync({
        sourceId: withdrawing.source.source_id,
        declarationId,
        reason: withdrawReason,
      });
      setWithdrawing(null);
      setWithdrawReason("");
    } catch (err) {
      setRefusal(err);
    }
  }

  const navigate = useNavigate();
  const tabSearch: { tab?: string; site?: string; state?: string } = useSearch({ strict: false });
  const tab: TabKey = isTab(tabSearch.tab) ? tabSearch.tab : "sources";
  /** **The census says what each window is; `GET /coverage` says a document may be waiting beside
   * it** (ADR-0166). Read only where the annotation is drawn - the Coverage tab - and separately
   * from the census, so its failure is said in place and never hides the six states or the
   * register's counts, which are still `GET /sources`. */
  const waitingRead = useCoverageRollup(current.period_start, current.period_end, tab === "coverage");
  /** A site and/or a window state the link asked for (the Data health dashboard's drills). */
  const clearNarrowing = () =>
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, site: undefined, state: undefined }),
    });
  /** Switch view. A push, so Back returns to the view the reader left. **Always explicit**: this screen stands in for Processing's Missing tab (ADR-0212), whose absent-`tab` is To review, so even its first view names itself. */
  /** Counted up to ask the declare-a-source form to open itself from outside. */
  const [declareSignal, setDeclareSignal] = useState(0);
  const moveTo = (key: TabKey) => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: key }),
    });
  };
  /** Any move the reader makes drops a pending "open the declare form" request, so coming back to
   * the Sources tab later finds the form closed rather than re-opened by a press from earlier. */
  const goTab = (key: TabKey) => {
    setDeclareSignal(0);
    moveTo(key);
  };
  /** A row to put focus on once its tab has drawn it. A connection between tabs is a move and then
   * a jump, and the target is not in the document until the move has happened - and for the
   * registers not until they have been read - so the jump waits for it rather than failing. */
  const [jump, setJump] = useState<{ id: string } | null>(null);
  const setPendingJump = (id: string) => setJump({ id });
  useEffect(() => {
    if (!jump) return;
    if (jumpTo(jump.id)) return;
    const watcher = new MutationObserver(() => {
      if (jumpTo(jump.id)) watcher.disconnect();
    });
    watcher.observe(document.body, { childList: true, subtree: true });
    // Give up quietly: a target that never appears (a failed read of the registers) must not
    // leave a watcher that grabs focus whenever something does turn up later.
    const giveUp = setTimeout(() => watcher.disconnect(), 4000);
    return () => {
      watcher.disconnect();
      clearTimeout(giveUp);
    };
  }, [jump]);

  if (isPending) {
    return (
      <ScreenFrame screen={screen} title="Coverage" crumb="what we expect" bodyScrolls>
        <SourcesOverview />
      </ScreenFrame>
    );
  }

  if (error || !data) {
    return (
      <ScreenFrame screen={screen} title="Coverage" crumb="what we expect">
        <Panel title="Coverage" column="1 / 13">
          {/* **A way out, not just a sentence** (EXP-29). This said the census could not be read
              and offered nothing but a page reload, which on the screen that decides whether a
              filing is complete reads as the product being broken. */}
          <ReadFailed of="The expected bills" error={error} onRetry={() => void retryCensus()} />
        </Panel>
      </ScreenFrame>
    );
  }

  const { completeness } = data;
  // The streams the URL asked for (`site`, `state`), or all of them. Everything below that draws
  // streams reads `view`; the census counts at the head of the screen stay for the whole window.
  const narrowing = narrowCoverage(data, { site: tabSearch.site, state: tabSearch.state });
  const view = narrowing.data;
  // **Counted from the same rows the register draws**, never from a second query: a chip that says
  // "With a gap 3" and lists two is a count and a list that disagree.
  const gapCount = view.sources.filter((s) => s.windows.some((w) => w.state === "missing")).length;
  const unownedCount = view.sources.filter((s) => s.owner_user_id === null).length;
  const listed = view.sources.filter((s) =>
    show === "gaps"
      ? s.windows.some((w) => w.state === "missing")
      : show === "unowned"
        ? s.owner_user_id === null
        : true,
  );

  /** Put the reader on a stream's own row on the Sources tab, **widening the register first if the
   * row is not drawn** (a filter, the ceiling): a jump from the grid should land, and the grid
   * lists every stream the register might have filtered out. */
  const revealStream = (source: SourceCoverage) => {
    const drawn = listed.slice(0, ceiling).some((entry) => entry.source_id === source.source_id);
    if (!drawn) {
      const at = view.sources.findIndex((entry) => entry.source_id === source.source_id);
      setShow("all");
      setCeiling((current) => Math.max(current, Math.ceil((at + 1) / SOURCE_CEILING) * SOURCE_CEILING));
    }
    setPendingJump(`source-${source.source_id}`);
    goTab("sources");
  };
  /** What the parts of another tab call to reach a row here. `openStream` says *no* when the row is
   * not among those listed so the caller can say so; `openAccount` cannot know before the registers
   * have been drawn, so it queues the jump and answers yes. */
  const nav = {
    openStream: (sourceId: string) => {
      if (!listed.slice(0, ceiling).some((entry) => entry.source_id === sourceId)) return false;
      setPendingJump(`source-${sourceId}`);
      goTab("sources");
      return true;
    },
    openAccount: (accountId: string) => {
      setPendingJump(`account-${accountId}`);
      goTab("billing");
      return true;
    },
  };

  return (
    <ScreenFrame
      screen={screen}
      title="Coverage"
      crumb={`what we expect · ${shortDate(data.period_start)} – ${shortDate(data.period_end)}`}
      // **Four panels now, which is one more than the two-row grid holds** (design principle 1:
      // *"a screen with more panels than the two-row grid holds opts into `bodyScrolls`"*).
      // ADR-0062's "Who bills what" was added without this, and the result was not a scrollbar
      // appearing — the grid resolved its rows to `263px 0px 541px`, so **Expected sources
      // collapsed to 2px** and the registers painted over it. Every control on the census was
      // then unreachable: "Show what arrived", the missing-period buttons and the nil
      // declarations, for a real Approver at 1440x900 and not only for the gate.
      //
      // Found by `ui-check` stopping deterministically on a click whose target had no height,
      // which is the gate earning its keep: the screen rendered, the tests passed, and the page
      // was unusable.
      bodyScrolls
    >
      <PageHeader
        subtitle="What we expect to receive, and what has not arrived."
        tabs={
          <RecordTabs
            tabs={TABS}
            tab={tab}
            onSelect={(key) => goTab(key as TabKey)}
            label="Sources views"
            saying={`Showing ${TABS.find((entry) => entry.key === tab)?.label ?? "Sources"}`}
          />
        }
        actions={
          // **Only for a role the server lets declare** (design rule 6) - the same tuple as nil.
          mayDeclare ? (
            <button
              type="button"
              className={own.ghost}
              onClick={() => {
                setDeclareSignal((count) => count + 1);
                setPendingJump("declare-expected-anchor");
                moveTo("sources");
              }}
            >
              Add an expected source
            </button>
          ) : undefined
        }
      />
      <SourcesOverview
        data={data}
        onShowGaps={() => goTab("coverage")}
        onShowUnowned={() => {
          setShow("unowned");
          setCeiling(SOURCE_CEILING);
          goTab("sources");
        }}
      />

      {/* **A stopped feed is above everything else on the screen** (COL-04, ADR-0123), because a
          census of what arrived says nothing about a feed that has stopped arriving. Renders
          nothing when no feed exists - and a feed that is not there is not a healthy one. */}
      <IngestionPanel />

      {/* The verdict, in a sentence rather than as a percentage, **above the tabs so it is read on
          every one of them**. "94% complete" reads as a pass; "3 windows missing" reads as three
          things to chase. And the second clause is the one that matters: a gap is *absent* from
          every total, not zero. The long caveats about what this census cannot say are ranked
          below it, in Basis and limits on the Coverage tab. */}
      <div className={cov.verdictBar} data-testid="sources-verdict">
        {completeness.sources === 0 ? (
          <StatusPill tone="neutral">Coverage unknown</StatusPill>
        ) : completeness.missing > 0 ? (
          <StatusPill tone="warn">
            {completeness.missing} window{completeness.missing === 1 ? "" : "s"} missing
          </StatusPill>
        ) : (
          <StatusPill tone="ok">Nothing outstanding</StatusPill>
        )}
        <div className={cov.verdictText}>
          <p>
            {completeness.sources === 0 ? (
              <span>No sources have been declared, so coverage remains unknown.</span>
            ) : completeness.missing > 0 ? (
              <span className={cov.verdictGap}>
                {completeness.missing} period{completeness.missing === 1 ? " is" : "s are"} missing.
                Emissions for {completeness.missing === 1 ? "it" : "them"} are absent from every
                total on every other screen — not zero, absent.
              </span>
            ) : (
              <span>Nothing expected is outstanding for this period.</span>
            )}{" "}
            {completeness.unknown > 0 ? (
              <>
                {completeness.unknown} on-event source
                {completeness.unknown === 1 ? " has" : "s have"} nothing recorded, which may mean
                nothing happened — that is not counted as a gap.
              </>
            ) : null}
          </p>
          {/* **The onboarding clause, as its own sentence** (COM-02): a reconciled census *and*
              named operating owners. Separate from the verdict above because the two fail
              independently and go to different people — missing data is a collection problem and an
              unnamed owner is a staffing decision. */}
          <p data-testid="onboarding">
            {completeness.unowned > 0 ? (
              <span className={cov.verdictGap}>{completeness.onboarding_sentence}</span>
            ) : (
              <span>{completeness.onboarding_sentence}</span>
            )}
          </p>
          {data.clamped_by_engagement ? (
            <p>
              Narrowed to your engagement: {shortDate(data.period_start)} –{" "}
              {shortDate(data.period_end)}.
            </p>
          ) : null}
        </div>
      </div>

      {narrowing.active || narrowing.ignored.length > 0 ? (
        <div className={cov.narrowing} data-testid="coverage-narrowing" role="status">
          {narrowing.active ? (
            <p data-testid="coverage-narrowing-says">
              Showing {view.sources.length} of {data.sources.length}{" "}
              {data.sources.length === 1 ? "stream" : "streams"}
              {narrowing.site ? <> at <strong>{narrowing.site.name}</strong></> : null}
              {narrowing.state ? (
                <>
                  {" "}with at least one <strong>{STATE[narrowing.state]?.short ?? narrowing.state}</strong>{" "}
                  window
                </>
              ) : null}
              . The counts above are for the whole reporting window.
            </p>
          ) : null}
          {narrowing.ignored.map((entry) => (
            <p key={entry.key} data-testid={`coverage-ignored-${entry.key}`}>
              {entry.key === "site"
                ? "The site in the link has no declared source in this window, so it is not applied."
                : "The state in the link is not one the expected-bills check has, so it is not applied."}
            </p>
          ))}
          <button type="button" className={own.ghost} onClick={clearNarrowing} data-testid="coverage-narrowing-clear">
            Show every stream
          </button>
        </div>
      ) : null}

      <div className={cov.tabArea}>
        <RecordPanel tab={tab}>
          <SourcesNavContext.Provider value={nav}>
          {tab === "coverage" ? (
            <div className={cov.stack}>
              {narrowing.active && view.sources.length === 0 ? (
                <Panel title="Coverage" column="1 / 13">
                  <Empty>No stream matches that site and period state.</Empty>
                </Panel>
              ) : (
              <SourcesHeatmap
                data={view}
                waiting={{
                  rollup: waitingRead.data,
                  pending: waitingRead.isPending,
                  error: waitingRead.error,
                  onRetry: () => void waitingRead.refetch(),
                }}
                mayDeclare={mayDeclare}
                onOpenStream={revealStream}
                onDeclare={(source, window) => {
                  setMessage(null);
                  setRefusal(null);
                  setDeclaring({ source, window });
                  setReason("");
                  setWithdrawing(null);
                  revealStream(source);
                }}
                onWithdraw={(source, window) => {
                  setMessage(null);
                  setRefusal(null);
                  setWithdrawing({ source, window });
                  setWithdrawReason("");
                  setDeclaring(null);
                  revealStream(source);
                }}
              />
              )}
              <div className={cov.pair}>
                <SourcesSiteCoverage data={view} />
                <Panel title="Basis and limits" sub="What this comparison can and cannot say">
                  <details className={cov.basis} data-testid="sources-basis">
                    <summary>Read the basis and limits</summary>
                    <p>
                      This compares the inventory against the sources somebody declared. Whether
                      that list covers the whole business is a judgement only you can make.
                    </p>
                    {completeness.unknown > 0 ? (
                      <p>
                        An on-event source with nothing in it is <strong>unknown</strong>, not
                        missing: nothing arriving may mean nothing was serviced, and the product
                        cannot tell which.
                      </p>
                    ) : null}
                    <p>
                      A period that has not closed is <strong>pending</strong>, never missing.
                      Submitted means activity arrived for the window; it is not a verification of
                      any figure or document.
                    </p>
                    {completeness.missing > 0 && !mayDeclare ? (
                      <p>
                        A missing period is either data still to arrive or a period with genuinely
                        no activity. Recording the second is an Approver&rsquo;s decision, so you
                        can read a window here but not declare it nil.
                      </p>
                    ) : null}
                  </details>
                </Panel>
              </div>
            </div>
          ) : null}
          {tab === "sources" ? (
          <div className={cov.stack}>

      <Panel title="Expected sources" sub="One row per data stream" column="1 / 13" flush>
        {completeness.missing > 0 && !mayDeclare ? (
          <div className={own.pad}>
            <p className={own.formNote}>
              A missing period is either data still to arrive or a period with genuinely no activity.
              Recording the second is an Approver&rsquo;s decision, so the cells below are not
              actionable for you.
            </p>
          </div>
        ) : null}
        {/* **COM-02's second half, said once rather than on every row.** The count above says how
            many sources have nobody named; this says whose act naming one is, and the two things
            about it a reader has to know before using the control:

            - **Removing an owner is a recorded act and not an error.** The alternative — refusing
              it until a replacement is named — keeps a name on a source that is no longer true,
              which is the version that reports 100% owned for ever.
            - **Nobody named never holds a close.** `is_onboarded` is a separate property from
              `is_complete` rather than a stricter one, because a close is gated on the data that
              arrived (CLS-01) and gating it on staffing would refuse a filing because somebody
              left. Said here because this is the screen where the two counts sit side by side and
              a reader could otherwise infer the gate from the adjacency. */}
        <div className={own.pad}>
        {mayAssignOwner ? (
          <p className={own.formNote} data-testid="owner-note">
            {mayPickOwner ? (
              <>
                Removing an owner is a recorded act and never an error: a supply whose owner has
                left belongs back on the onboarding list.
              </>
            ) : isAdmin ? (
              <>
                Until the roster is read you can still take a source&rsquo;s ownership or remove an
                owner. Naming a colleague needs that list.
              </>
            ) : (
              <>
                You can take a source&rsquo;s ownership, or remove whoever holds it. Naming a{" "}
                <strong>colleague</strong> needs the workspace roster, which only an{" "}
                <strong>Admin</strong> may read — so a source somebody else owns reads as{" "}
                <em>a colleague</em> here rather than by name.
              </>
            )}{" "}
            Nobody named does not hold a close: a close is gated on the data that arrived, not on
            who was staffed to chase it.
          </p>
        ) : (
          <p className={own.formNote} data-testid="owner-who-can">
            An <strong>Approver</strong> or an <strong>Admin</strong> names the operating owner for
            a source. Nobody named does not hold a close: a close is gated on the data that
            arrived, not on who was staffed to chase it.
          </p>
        )}
        {/* A failed roster read, **once and with a way out** (EXP-29) — not an alert per row, and
            not an empty picker. The rows keep the two controls that need no list, so the capability
            survives the failed read rather than disappearing with it. */}
        {isAdmin && roster.error ? (
          <div data-testid="owner-roster-failed">
            <ReadFailed
              of="The people in this workspace"
              error={roster.error}
              onRetry={() => void roster.refetch()}
            />
          </div>
        ) : null}
        </div>
        {data.sources.length === 0 ? (
          <div className={own.pad}>
            <Empty>
              No sources have been declared, so completeness is <strong>unknown</strong> rather than
              complete. A total here would be a sum over whatever happened to arrive.
            </Empty>
          </div>
        ) : (
          <div className={reg.table} data-testid="expected-sources">
            <div className={reg.toolbar} role="group" aria-label="Which streams to list">
              {(
                [
                  ["all", "All streams", view.sources.length],
                  ["gaps", "With a gap", gapCount],
                  ["unowned", "No owner", unownedCount],
                ] as const
              ).map(([key, label, count]) => (
                <button
                  key={key}
                  type="button"
                  className={reg.filter}
                  aria-pressed={show === key}
                  onClick={() => {
                    setShow(key);
                    setCeiling(SOURCE_CEILING);
                  }}
                >
                  {label} <span className={reg.filterCount}>{count}</span>
                </button>
              ))}
              <span className={reg.toolbarNote} data-testid="sources-showing">
                Showing {Math.min(listed.length, ceiling)} of {listed.length}
                {show === "all" ? "" : ` (${view.sources.length} in all)`}
              </span>
            </div>
            <div className={`${reg.head} ${own.cols}`} aria-hidden="true">
              <span>Stream</span>
              <span>Operating owner</span>
              <span>Billed by</span>
              <span />
            </div>
            {listed.slice(0, ceiling).map((source) => (
              <div
                key={source.source_id}
                id={`source-${source.source_id}`}
                tabIndex={-1}
                data-testid={`source-row-${source.source_id}`}
                className={`${reg.row} ${own.cols} ${own.sourceRow} ${
                  expanded === source.source_id ||
                  declaring?.source.source_id === source.source_id ||
                  withdrawing?.source.source_id === source.source_id
                    ? reg.rowOpen
                    : ""
                }`}
              >
                <div className={own.rowHead}>
                  <span className={own.rowName}>
                    {/* The source's own page (Batch 5): every window, and what satisfied it. */}
                    <Link
                      to="/sources/$sourceId"
                      params={{ sourceId: source.source_id }}
                      search={keepSearch}
                      className={own.link}
                      data-testid={`source-page-${source.source_id}`}
                    >
                      {nameOf(source)}
                    </Link>
                  </span>
                  <span className={own.rowMeta}>
                    {source.facility_name} · {source.cadence.replace(/_/g, "-")}
                    {source.meter_label && source.meter_label !== nameOf(source)
                      ? ` · ${source.meter_label}`
                      : ""}
                    {source.is_retired
                      ? ` · retired ${shortDate(source.expected_to)}`
                      : ""}
                  </span>
                </div>
                <SourceOwner
                  source={source}
                  people={people}
                  mayAssign={mayAssignOwner}
                  mayPick={mayPickOwner}
                  meId={me?.user_id}
                  from={current.period_start}
                  to={current.period_end}
                  onReload={() => void retryCensus()}
                />
                <SupplyLine source={source} />
                {/* **The derived end of this source's life, and what ended it** (CTX-03).
                    `expected_to` above is this stream's own retirement and answers a different
                    question: a meter that came out or a site that closed ends the life without
                    writing anything here, and before this the screen showed a run of `not expected`
                    windows with nothing saying why they were excused — which reads as the census
                    having lost interest.

                    Rendered only where the basis is **not** the declaration: there, `life_to` is
                    `expected_to` and the pair above already says it, with the person's reason under
                    the row. A second sentence repeating one date would be the kind of duplicate
                    that eventually disagrees with itself. */}
                {source.life_to && source.life_to_basis !== "declaration" ? (
                  <p
                    className={own.lifeEnd}
                    data-testid={`life-${source.source_id}`}
                    title={LIFE_BASIS[source.life_to_basis ?? ""]?.remedy ?? ""}
                  >
                    Nothing expected after {shortDate(source.life_to)} —{" "}
                    {LIFE_BASIS[source.life_to_basis ?? ""]?.said ?? source.life_to_basis}.
                  </p>
                ) : null}
                <div className={own.strip}>
                  {source.windows.map((window) => {
                    const answerable = window.state === "missing" && mayDeclare;
                    /** **A nil an Approver can take back** (ADR-0064). Only where the window
                     * carries the id of the declaration behind it — a cell with no id has nothing
                     * to withdraw, and a control that predictably fails is the defect UX-04 names. */
                    const withdrawable =
                      window.state === "declared_nil" &&
                      mayDeclare &&
                      Boolean(window.nil_declaration_id);
                    return (
                      <button
                        key={window.period_start}
                        type="button"
                        disabled={!answerable && !withdrawable}
                        className={`${own.cell} ${own[window.state] ?? ""} ${
                          answerable ? own.actionable : ""
                        } ${withdrawable ? own.withdrawable : ""}`}
                        data-withdrawable={withdrawable ? "yes" : undefined}
                        title={`${shortDate(window.period_start)} – ${shortDate(
                          window.period_end,
                        )}: ${STATE[window.state]?.long ?? window.state}${
                          window.nil_reason ? ` — “${window.nil_reason}”` : ""
                        }${withdrawable ? ". Click to take it back." : ""}`}
                        // The state **name** is in the label as well as the explanation. A screen
                        // reader hears "Q2: missing — it should have arrived by now", which is the
                        // same two pieces of information a sighted reader gets from the cell and
                        // the legend.
                        aria-label={`${period(window, source.cadence)}: ${
                          STATE[window.state]?.short ?? window.state.replace(/_/g, " ")
                        } — ${STATE[window.state]?.long ?? ""}`}
                        onClick={() => {
                          setMessage(null);
                          setRefusal(null);
                          if (window.state === "declared_nil") {
                            setWithdrawing({ source, window });
                            setWithdrawReason("");
                            setDeclaring(null);
                            return;
                          }
                          setDeclaring({ source, window });
                          setReason("");
                          setWithdrawing(null);
                        }}
                      >
                        <StateGlyph state={window.state} />
                        {period(window, source.cadence)} {STATE[window.state]?.short}
                      </button>
                    );
                  })}
                </div>
                {source.retired_reason ? (
                  <p className={own.formNote}>{source.retired_reason}</p>
                ) : null}

                {/* **EXP-06.** The census says *3 submitted*; this says which three, and links to
                    each. Without it a reader verifying that claim has to filter the register by
                    site, then carrier, then period, and hope the result is the set the census
                    counted — and the two match on different keys, so it frequently is not.

                    Expanded on request rather than always: thirty sources would be thirty queries
                    on a screen whose job is a single comparison. */}
                {/* **EXP-06.** The census says *3 submitted*; "what arrived" says which three, and
                    links to each. Expanded on request rather than always: thirty sources would be
                    thirty queries on a screen whose job is a single comparison.

                    **The control `useRetireSource` was waiting for** (F-006) sits beside it: stop
                    expecting this stream, from a date. See `SourceRetirement` for what it does and
                    does not touch - the meter and the site are retired on Organisation. Both are
                    handed to it as children because they share one actions column, and the
                    component renders them whether or not the reader may retire. */}
                <SourceRetirement
                  source={source}
                  name={nameOf(source)}
                  from={current.period_start}
                  to={current.period_end}
                  mayRetire={mayRetire}
                  onReload={() => void retryCensus()}
                >
                  <button
                    type="button"
                    className={reg.act}
                    aria-expanded={expanded === source.source_id}
                    onClick={() =>
                      setExpanded(expanded === source.source_id ? null : source.source_id)
                    }
                  >
                    {expanded === source.source_id ? "Hide" : "Show"} what arrived
                  </button>
                  {/* The stream's own record page (F-007): its tasks, notes, flags and approvals. The
                      links *inside* "what arrived" go to the activity records that arrived; this is
                      the one for the expectation itself, which is a different record. */}
                  <RecordLink
                    recordId={source.source_id}
                    kind="expected_source"
                    subject={nameOf(source)}
                  />
                </SourceRetirement>
                {expanded === source.source_id ? (
                  <SourceLinksPanel
                    supply={source.supply}
                    sourceId={source.source_id}
                    from={current.period_start}
                    to={current.period_end}
                    meId={me?.user_id}
                  />
                ) : null}

                {withdrawing?.source.source_id === source.source_id ? (
                  <div className={own.form} data-testid="withdraw-nil">
                    <p className={own.formHead}>
                      Take back the confirmation that none was due for {shortDate(withdrawing.window.period_start)} –{" "}
                      {shortDate(withdrawing.window.period_end)}?
                    </p>
                    {withdrawing.window.nil_reason ? (
                      <p className={own.formNote} data-testid="withdraw-what-was-said">
                        It said: &ldquo;{withdrawing.window.nil_reason}&rdquo;
                      </p>
                    ) : null}
                    <p className={own.formNote}>
                      The declaration stays on record, with who said it and when — this adds your
                      reason beside it. The period then goes back to whatever the expected-bills check finds,
                      which is often <strong>missing</strong>, because that is what it now is. It
                      does <strong>not</strong> become a zero.
                    </p>
                    <input
                      className={own.input}
                      aria-label="Why was that declaration wrong?"
                      value={withdrawReason}
                      onChange={(event) => setWithdrawReason(event.target.value)}
                      placeholder="e.g. the March invoice arrived and shows 410 therms"
                    />
                    <div className={own.buttons}>
                      <button
                        type="button"
                        className={styles.primary}
                        disabled={!withdrawReason.trim() || withdraw.isPending}
                        onClick={() => void onWithdraw()}
                      >
                        {withdraw.isPending ? "Withdrawing…" : "Withdraw the confirmation"}
                      </button>
                      <button
                        type="button"
                        className={own.ghost}
                        // Cancel puts the form back as it was — a reason left in state would
                        // reappear on the next cell, as an explanation for a different statement.
                        onClick={() => {
                          setWithdrawing(null);
                          setWithdrawReason("");
                          setRefusal(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {/* The reason above is already the Admin's act (ADR-0094), so this only tells
                        everyone else whose it is. */}
                    <PostCloseRetry error={refusal} />
                    <WriteFailed
                      error={isPeriodClosed(refusal) ? null : refusal}
                      of="this source's expected bills"
                      onReload={() => {
                        void retryCensus();
                        setWithdrawing(null);
                        setWithdrawReason("");
                        setRefusal(null);
                      }}
                    />
                  </div>
                ) : null}

                {declaring?.source.source_id === source.source_id ? (
                  <div className={own.form}>
                    <p className={own.formHead}>
                      Nothing happened between {shortDate(declaring.window.period_start)} and{" "}
                      {shortDate(declaring.window.period_end)}?
                    </p>
                    <p className={own.formNote}>
                      This records a <strong>declared zero</strong>, which is a different thing from
                      a gap: the period stops being missing and the reason travels with it. Say how
                      you know — it is the first thing an assurance provider asks.
                    </p>
                    <input
                      className={own.input}
                      aria-label="Why was there no activity?"
                      value={reason}
                      onChange={(event) => setReason(event.target.value)}
                      placeholder="e.g. the generator was not run; the test log records no hours"
                    />
                    <div className={own.buttons}>
                      <button
                        type="button"
                        className={styles.primary}
                        disabled={!reason.trim() || declare.isPending}
                        onClick={() => void onDeclare()}
                      >
                        {declare.isPending ? "Recording…" : "Confirm none"}
                      </button>
                      <button
                        type="button"
                        className={own.ghost}
                        // **Cancel restores the prior state**: the panel closed, the typed reason
                        // gone and any refusal with it. A reason left in state reappears on the
                        // next window somebody clicks, as an explanation for a different gap.
                        onClick={() => {
                          setDeclaring(null);
                          setReason("");
                          setMessage(null);
                          setRefusal(null);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                    {/* The reason above is already the Admin's act (ADR-0094), so this only tells
                        everyone else whose it is. */}
                    <PostCloseRetry error={refusal} />
                    <WriteFailed
                      error={isPeriodClosed(refusal) ? null : refusal}
                      of="this source's expected bills"
                      onReload={() => {
                        void retryCensus();
                        setDeclaring(null);
                        setReason("");
                        setRefusal(null);
                      }}
                    />
                  </div>
                ) : null}
              </div>
            ))}
            {/* **The ceiling, said, with the control that raises it.** Cutting the list without
                saying so would read as the whole census; this is the count of what is not drawn. */}
            {listed.length > ceiling ? (
              <div className={reg.ceiling} data-testid="sources-ceiling">
                <span>
                  Showing {ceiling} of {listed.length} streams.
                </span>
                <button
                  type="button"
                  className={reg.act}
                  onClick={() => setCeiling(ceiling + SOURCE_CEILING)}
                >
                  Show {Math.min(SOURCE_CEILING, listed.length - ceiling)} more
                </button>
                <button type="button" className={reg.act} onClick={() => setCeiling(listed.length)}>
                  Show all {listed.length}
                </button>
              </div>
            ) : null}
            {listed.length === 0 ? (
              <div className={own.pad}>
                <Empty>Nothing matches that filter.</Empty>
              </div>
            ) : null}
          </div>
        )}
        <div className={own.pad}>
        {/* Said once, for the panel (F-006, design rule 6): the per-row control is withheld from a
            role that would collect a 403, and this is the sentence that says whose act it is. */}
        {mayRetire === false && data.sources.length > 0 ? (
          <p className={own.formNote} data-testid="retire-who-can">
            An <strong>Approver</strong> or an <strong>Admin</strong> stops a source being expected,
            from a date. It is a statement about what the business does — which is why it sits with
            confirming none was due — and nothing is deleted when they do.
          </p>
        ) : null}

        {/* **B-23's last piece of onboarding.** The census can only be as complete as the list of
            streams somebody declared, and until this existed every one came from the seeder.

            Here rather than on the Organisation screen, because this is where the consequence is
            read: somebody looking at a gap is the person who knows what else should be arriving.
            An **Approver's** act for the same reason declaring a nil is — it is a statement about
            what the business does, not a structural edit. */}
        {/* **The next action's confirmation, where it can be seen.** `onDone` wrote this sentence
            into the same slot the nil form used, which only renders while a nil form is open — so
            declaring a new expected source succeeded and said nothing anywhere. A confirmation
            nobody can see is the same defect as an absence left blank. */}
        {message ? (
          <p className={own.formNote} role="status" data-testid="declared-expected">
            {message}
          </p>
        ) : null}
        {mayDeclare ? (
          <div id="declare-expected-anchor" tabIndex={-1}>
            <DeclareExpected
              from={current.period_start}
              to={current.period_end}
              onDone={setMessage}
              openSignal={declareSignal}
            />
          </div>
        ) : (
          <p className={own.formNote}>
            An <strong>Approver</strong> declares what the expected-bills check should expect. A missing stream is
            invisible until somebody says it should be there.
          </p>
        )}
        </div>
      </Panel>
          </div>
          ) : null}

          {/* **ADR-0062.** Who bills which meter, in the screen where the consequence is read:
              ending an account ends every stream beneath it, and the row on the Sources tab says
              which act did it. */}
          {tab === "billing" ? (
            <div className={cov.stack}>
              <SourceRegisters mayDeclare={mayDeclare} streams={data.sources} />
            </div>
          ) : null}
          </SourcesNavContext.Provider>
        </RecordPanel>
      </div>
    </ScreenFrame>
  );
}

/** Who is accountable for this source arriving — **COM-02**.
 *
 * `expected_source.owner_user_id` sat in the schema with a route writing it, a census counting it
 * and **no way to set one from the product**, which made the count a figure that could never move.
 *
 * Four decisions, three of them the server's and honoured rather than re-made here.
 *
 * **Remove is an option and not a consequence.** In the picker it is the *first* option, and it
 * writes `null`, which the route accepts as a value. Hiding it behind "name a replacement" keeps a
 * name on a source that is no longer true — the version that reports 100% owned for ever.
 *
 * **The name comes from the roster, and a missing roster is said rather than filled.** `people`
 * being `undefined` means the list was not read, so the owner renders as *a colleague*; it never
 * renders the UUID, and it never renders "nobody", which would be a different fact entirely.
 *
 * **It is one component per row so the refusal is per row.** One shared mutation would disable
 * every other source's control while one was saving, and would put one source's refusal under
 * another's name.
 *
 * **Its own mutation and no local count.** `useAssignOwner` invalidates the census, so the "No
 * owner" figure above moves because the server recounted — not because this component decremented
 * anything.
 */
export function SourceOwner({
  source,
  people,
  mayAssign,
  mayPick,
  meId,
  from,
  to,
  onReload,
}: {
  source: SourceCoverage;
  /** `undefined` means *the roster was not read*, which is not the same as nobody being in it. */
  people: Member[] | undefined;
  mayAssign: boolean;
  mayPick: boolean;
  meId: string | undefined;
  from: string;
  to: string;
  onReload: () => void;
}) {
  const assign = useAssignOwner(from, to);
  const [refusal, setRefusal] = useState<unknown>(null);
  const owner = source.owner_user_id;
  const personName = useNameOf();
  const named = owner === null ? undefined : people?.find((person) => person.user_id === owner);
  /** Four states and four sentences. *Nobody* is a fact about the source; *a colleague* is a fact
   * about what this reader may see, and collapsing the two would report an unread roster as an
   * unowned supply — a gap the census does not have. */
  const held =
    owner === null
      ? "Nobody"
      : owner === meId
        ? "You"
        : (named?.display_name ?? personName(owner) ?? A_COLLEAGUE);

  async function set(ownerUserId: string | null) {
    setRefusal(null);
    try {
      await assign.mutateAsync({ sourceId: source.source_id, ownerUserId });
    } catch (error) {
      setRefusal(error);
    }
  }

  /** The owner's own option, when the roster does not contain them.
   *
   * A `<select>` whose `value` matches no option is an uncontrolled one: the browser shows the
   * first entry, so an owned source would render as **Nobody** and one stray click would unassign
   * somebody the reader never saw. It should not happen — the route refuses a non-member — but a
   * suspended or erased account is exactly the row that goes missing from a list, and the failure
   * is silent.
   */
  const options: Array<{ id: string; label: string }> =
    owner !== null && named === undefined
      ? [{ id: owner, label: held }, ...(people ?? []).map(asOption)]
      : (people ?? []).map(asOption);

  return (
    // `data-owned` is on the same element as the testid, so a check reads the fact and the
    // element in one selector — and it is the *fact*, not the colour: a reader who cannot see
    // `--warn` still gets the word, and a browser check still gets the attribute.
    <div
      className={own.owner}
      data-testid={`owner-${source.source_id}`}
      data-owned={owner === null ? "no" : "yes"}
    >
      <span className={own.ownerLabel}>Operating owner</span>
      {/* **Its own id, because the picker is inside the same element.** A check reading the row's
          text to assert the owner's name finds it among the `<option>` labels whether the row
          resolved one or not — an assertion that cannot fail, which is the defect this repo has
          now found seven times. */}
      <span
        className={owner === null ? own.ownerNobody : own.ownerValue}
        data-testid={`owner-held-${source.source_id}`}
      >
        {held}
      </span>

      {mayPick ? (
        <select
          className={own.ownerPick}
          data-testid={`owner-pick-${source.source_id}`}
          aria-label={`Operating owner for ${nameOf(source)}`}
          value={owner ?? ""}
          disabled={assign.isPending}
          onChange={(event) => void set(event.target.value || null)}
        >
          {/* **First, and phrased as the act it is.** Not a blank entry at the top of a list: a
              blank reads as "no choice made" and this is a choice — somebody is recording that
              this supply has no owner, which is what puts it back on the onboarding list. */}
          <option value="">Nobody — remove the owner</option>
          {options.map((person) => (
            <option key={person.id} value={person.id}>
              {person.label}
            </option>
          ))}
        </select>
      ) : mayAssign ? (
        <>
          {/* **The two acts that need no roster**, for the role that may assign and may not read
              the list of people. Not a disabled picker and not a free-text name: the owner must be
              a member (checked at the route under the caller's own scope), so a box somebody could
              type into would be a control whose refusal is guaranteed for most inputs. */}
          {/* **`meId === undefined` renders nothing, rather than falling back.** `meId ?? null`
              was what this said, and `null` is the *remove* value — a button labelled "Take
              ownership" that unassigns whoever holds the source. It cannot happen while
              `mayAssign` is derived from the same session, which is exactly why a fallback here
              would have survived every test. */}
          {meId === undefined || owner === meId ? null : (
            <button
              type="button"
              className={own.ownerAction}
              data-testid={`owner-take-${source.source_id}`}
              disabled={assign.isPending}
              onClick={() => void set(meId)}
            >
              Take ownership
            </button>
          )}
          {/* Offered only where there is an owner to remove. First-class in that it is never gated
              on naming a replacement — not in that it is rendered against a source where it would
              do nothing, which is a control with no effect. */}
          {owner === null ? null : (
            <button
              type="button"
              className={own.ownerAction}
              data-testid={`owner-remove-${source.source_id}`}
              disabled={assign.isPending}
              onClick={() => void set(null)}
            >
              Remove the owner
            </button>
          )}
        </>
      ) : null}

      <WriteFailed
        error={refusal}
        of="this source's owner"
        className={own.ownerError}
        onReload={() => {
          setRefusal(null);
          onReload();
        }}
      />
    </div>
  );
}

function asOption(person: Member): { id: string; label: string } {
  return { id: person.user_id, label: person.display_name };
}

/** What the form offers before — or instead of — the factor library's answer.
 *
 * The four a first-time customer declares, with no carriers: used while the options load and when
 * they cannot be read, so the form is never an empty menu. An empty menu reads as *"nothing can be
 * expected here"*, which is a claim, and a failed read is not evidence of it.
 */
const FALLBACK_CATEGORIES = ["stationary", "mobile", "electricity", "fugitive"] as const;

/** Declaring a stream the census should expect — **B-23**.
 *
 * Its own component because it holds a form's worth of state, and because the panel it sits in is
 * already the longest on the screen.
 *
 * **Every refusal here is the server's own sentence.** They are written to be acted on — *"gas_oil
 * is not published for GB as a stationary source… Published: natural_gas"* names what to pick
 * instead — and replacing them with "Could not save" would throw that away.
 *
 * **The menu is the factor library's, and the field still takes typing.** What can be expected at a
 * site is what the library prices for its jurisdiction (F06): the category list and the carrier
 * suggestions come from `GET /sources/options`, so a form can no longer offer *purchased
 * electricity* on a gas carrier — which no reading would ever satisfy, and which the census would
 * report as a permanent gap that looked like a collection failure. The carrier stays a text input
 * with suggestions rather than a closed list on purpose: the server remains the authority and its
 * refusal names what *is* published, so a guess is one click from right.
 *
 * **Meter-level or site-level is a choice, and it is said.** *Any meter at this site* is how
 * `census._submitted` reads an expectation with no meter — satisfied by a reading from any supply
 * there. A meter-level one is satisfied only by that meter, and is also what an account's dates can
 * narrow.
 */
export function DeclareExpected({
  from,
  to,
  onDone,
  openSignal = 0,
}: {
  from: string;
  to: string;
  onDone: (message: string | null) => void;
  /** Counted up by the screen to open the form from outside (the header's *Add an expected
   * source*). A counter and not a boolean so a second press after cancelling opens it again. */
  openSignal?: number;
}) {
  const declare = useDeclareExpected(from, to);
  const facilities = useFacilities();
  // Open on arrival when the screen has been asked to open it (the form is not mounted while
  // another tab is showing, so the request is read here and not only when it changes).
  const [open, setOpen] = useState(openSignal > 0);
  const [facilityId, setFacilityId] = useState("");
  const [category, setCategory] = useState<ExpectedSourceIn["category"]>("stationary");
  const [carrier, setCarrier] = useState("natural_gas");
  const [cadence, setCadence] = useState<ExpectedSourceIn["cadence"]>("monthly");
  const [expectedFrom, setExpectedFrom] = useState(from);
  const [label, setLabel] = useState("");
  const [meterId, setMeterId] = useState("");
  const [months, setMonths] = useState<number[]>([]);
  const [refusal, setRefusal] = useState<unknown>(null);
  // Opened from outside when the signal changes: adjusted during render rather than in an effect,
  // so the form is open on the first paint after the press and not one render later.
  const [seenSignal, setSeenSignal] = useState(openSignal);
  if (openSignal !== seenSignal) {
    setSeenSignal(openSignal);
    if (openSignal > seenSignal) setOpen(true);
  }

  const sites = facilities.data ?? [];
  /** The site the form is *about* — the chosen one, or the first, which is what the select shows. */
  const siteId = facilityId || (sites[0]?.id ?? "");
  const options = useStreamOptions(open && siteId ? siteId : null);
  const meters = useMeters(open && siteId ? siteId : null);

  const offered = options.data?.categories;
  const categories: readonly string[] = offered
    ? offered.map((entry) => entry.category)
    : FALLBACK_CATEGORIES;
  /** The category the form is really about. If the site the reader just switched to is not priced
   * for the one they had chosen, the select shows its first option — and the submit must send that
   * one too, not the stale state behind it. */
  const chosen = categories.includes(category) ? category : (categories[0] ?? category);
  const published = offered?.find((entry) => entry.category === chosen)?.carriers;
  const liveMeters = (meters.data ?? []).filter((meter) => !meter.operational_to);

  /** Put the form back as it was, which is every field and not only the panel. A cancelled form
   * that keeps a carrier and a date reopens as somebody else's half-made declaration. */
  function cancel() {
    setOpen(false);
    setFacilityId("");
    setCategory("stationary");
    setCarrier("natural_gas");
    setCadence("monthly");
    setExpectedFrom(from);
    setLabel("");
    setMeterId("");
    setMonths([]);
    setRefusal(null);
  }

  /** A new category has its own carriers, so keeping the old one is how a gas carrier ends up on
   * an electricity stream. Replaced only when the library says the old one is not published for
   * the new category — a carrier that is still valid is left exactly as typed. */
  function chooseCategory(next: string) {
    setCategory(next as ExpectedSourceIn["category"]);
    const carriers = offered?.find((entry) => entry.category === next)?.carriers;
    if (carriers && carriers.length > 0 && !carriers.includes(carrier)) {
      setCarrier(carriers[0] ?? carrier);
    }
  }

  /** Declare the stream, optionally carrying an Admin's reason for a stream that starts inside a
   * closed period (ADR-0094). The form keeps what was typed, so the retry is one field. */
  function submit(postCloseReason?: string) {
    setRefusal(null);
    onDone(null);
    return declare
      .mutateAsync({
        facility_id: siteId,
        category: chosen as ExpectedSourceIn["category"],
        fuel_or_carrier: carrier,
        cadence,
        expected_from: expectedFrom,
        meter_id: meterId || null,
        seasonal_months: cadence === "on_event" ? [] : months,
        label: label || null,
        ...(postCloseReason ? { post_close_reason: postCloseReason } : {}),
      })
      .then(
        () => {
          onDone(`${label || carrier} is now expected. A period with nothing in it is a gap.`);
          setLabel("");
          setOpen(false);
        },
        // The refusal itself. These sentences are the server's and are written to be acted on
        // — *"gas_oil is not published for GB as a stationary source… Published:
        // natural_gas"* — and `WriteFailed` keeps them while adding the status, so a 403 is
        // not reported as a problem with the carrier somebody typed.
        (error: unknown) => setRefusal(error),
      );
  }

  if (!open) {
    return (
      <button
        type="button"
        className={own.declareOpen}
        data-testid="declare-expected-open"
        onClick={() => setOpen(true)}
      >
        Declare another expected source
      </button>
    );
  }

  return (
    <form
      className={own.declare}
      data-testid="declare-expected"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {/* The site list is a dependent read. It defaulted to `[]`, so a failed request rendered as
          an **empty picker on a form that submits the first site in it** — and with no sites in
          the list that is the empty string, which the server refuses as a malformed id. The
          failure read as a bad declaration rather than a failed read (EXP-29).

          **This is the first `<select>` in the form, and the browser gate selects it by position.** */}
      <Loaded
        query={facilities}
        of="The list of sites"
        empty="No sites are set up yet. Add one on Organisation before declaring what it should send."
      >
        {(loadedSites) => (
          <label className={own.field}>
            <span className={own.fieldLabel}>Site</span>
            <select
              className={own.input}
              value={facilityId || (loadedSites[0]?.id ?? "")}
              onChange={(event) => {
                setFacilityId(event.target.value);
                // A meter belongs to one site. Keeping the last site's would submit a meter at a
                // different site, which the server refuses as a permanent gap in waiting.
                setMeterId("");
              }}
            >
              {loadedSites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </Loaded>
      <label className={own.field}>
        <span className={own.fieldLabel}>What arrives</span>
        <select
          className={own.input}
          value={chosen}
          onChange={(event) => chooseCategory(event.target.value)}
        >
          {categories.map((value) => (
            <option key={value} value={value}>
              {CATEGORY_LABEL[value] ?? value.replace(/_/g, " ")}
            </option>
          ))}
        </select>
      </label>
      <label className={own.field}>
        <span className={own.fieldLabel}>Fuel or carrier</span>
        <input
          className={own.input}
          value={carrier}
          required
          list="declare-carriers"
          data-testid="carrier"
          onChange={(event) => setCarrier(event.target.value)}
        />
        <datalist id="declare-carriers">
          {(published ?? []).map((value) => (
            <option key={value} value={value} />
          ))}
        </datalist>
        {/* The refusal names what *is* published, so a wrong guess here is one click from right.
            And once the library has answered, a carrier outside its list is said before the
            click rather than after it — advice, never a block: the server stays the authority. */}
        {published && !published.includes(carrier) ? (
          <span className={own.formNote} data-testid="carrier-advice">
            For {(CATEGORY_LABEL[chosen] ?? chosen).toLowerCase()} at this site EmitIQ publishes{" "}
            {published.join(", ")}. Anything else will be refused.
          </span>
        ) : (
          <span className={own.formNote}>
            If EmitIQ cannot price it, the refusal lists what it does publish.
          </span>
        )}
        {options.error ? (
          <span className={own.formNote} data-testid="options-unread">
            What this site can be expected to send could not be read, so the list above is the
            usual four. EmitIQ still checks the carrier.
          </span>
        ) : null}
      </label>
      <label className={own.field}>
        <span className={own.fieldLabel}>Which meter</span>
        <select
          className={own.input}
          value={meterId}
          data-testid="declare-meter"
          onChange={(event) => setMeterId(event.target.value)}
        >
          <option value="">Any meter at this site</option>
          {liveMeters.map((meter) => (
            <option key={meter.id} value={meter.id}>
              {meter.label ?? meter.kind.replace(/_/g, " ")}
            </option>
          ))}
        </select>
        <span className={own.formNote}>
          {meterId
            ? "Only a reading from this meter satisfies it, and its account's dates apply."
            : "A reading from any meter at the site satisfies it."}
        </span>
        {/* A failed read is not "this site has no meters": the list above would be empty either way. */}
        {meters.error ? (
          <span className={own.formNote} data-testid="declare-meters-failed">
            This site&rsquo;s meters could not be read, so a particular meter cannot be chosen here.
            Close this form and open it again to ask once more.
          </span>
        ) : null}
      </label>
      <label className={own.field}>
        <span className={own.fieldLabel}>How often</span>
        <select
          className={own.input}
          value={cadence}
          onChange={(event) => setCadence(event.target.value as ExpectedSourceIn["cadence"])}
        >
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="annual">Annually</option>
          <option value="on_event">When something happens</option>
        </select>
      </label>
      <label className={own.field}>
        <span className={own.fieldLabel}>Expected from</span>
        <input
          type="date"
          className={own.input}
          value={expectedFrom}
          required
          onChange={(event) => setExpectedFrom(event.target.value)}
        />
        <span className={own.formNote}>
          A site acquired in August is not missing seven bills.
        </span>
      </label>
      {cadence === "on_event" ? null : (
        <fieldset className={own.field} data-testid="declare-season">
          <legend className={own.fieldLabel}>Only some months? (optional)</legend>
          <span className={own.monthRow}>
            {MONTHS.map((name, index) => {
              const month = index + 1;
              return (
                <label key={name} className={own.monthPick}>
                  <input
                    type="checkbox"
                    checked={months.includes(month)}
                    onChange={(event) =>
                      setMonths(
                        event.target.checked
                          ? [...months, month].sort((a, b) => a - b)
                          : months.filter((m) => m !== month),
                      )
                    }
                  />{" "}
                  {name}
                </label>
              );
            })}
          </span>
          <span className={own.formNote}>
            A heating meter read October to March is not missing six bills in summer. Leave them all
            unticked for a stream that reports all year.
          </span>
        </fieldset>
      )}
      <label className={own.field}>
        <span className={own.fieldLabel}>Label (optional)</span>
        <input
          className={own.input}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          placeholder="Trafford Park — gas supply"
        />
      </label>
      <div className={own.declareActions}>
        <button
          type="submit"
          className={own.declareSubmit}
          disabled={declare.isPending || !siteId}
        >
          {declare.isPending ? "Declaring…" : "Declare it"}
        </button>
        <button type="button" className={own.declareCancel} onClick={cancel}>
          Cancel
        </button>
      </div>
      {/* `declare-error` is kept as the id, because the gate already selects it and asserts both
          the sentence and that it renders wide enough to read. The **branch** is a separate
          attribute, so a check can still say which of the four refusals it expected. */}
      {/* A stream that starts inside a closed period (ADR-0094): an Admin may declare it with a
          reason, anyone else is told whose act it is. */}
      <PostCloseRetry
        error={refusal}
        pending={declare.isPending}
        onRecord={(reason) => void submit(reason)}
      />
      <WriteFailed
        error={isPeriodClosed(refusal) ? null : refusal}
        of="the expected-bills check"
        testId="declare-error"
        className={own.declareError}
        onReload={() => void facilities.refetch()}
      />
    </form>
  );
}

/** The readings and evidence behind one expected source — **EXP-06**.
 *
 * Its own component because it fetches: inlining the query in the row would run it for every
 * source whether or not anybody expanded one, which is the cost this feature exists to avoid.
 */
export function SourceLinksPanel({
  supply,
  sourceId,
  from,
  to,
  meId,
}: {
  supply: SourceCoverage["supply"];
  sourceId: string;
  from: string;
  to: string;
  meId: string | undefined;
}) {
  const { data, isPending, error, refetch } = useSourceLinks(sourceId, from, to);
  const retry = () => void refetch();
  if (isPending) return <p className={own.formNote}>Reading what arrived…</p>;
  // **A failed read had no way out but a page reload** (EXP-29). Same component as every other
  // failed read on the product, so the sentence and the remedy are written once.
  if (error) {
    return <ReadFailed of="What arrived for this source" error={error} onRetry={retry} />;
  }
  if (!data) return <p className={own.formNote}>Nothing has been recorded against this source.</p>;

  return (
    <>
    <div className={own.links} data-testid="source-links">
      <p className={own.formNote}>{data.sentence}</p>
      {/* **Connections** (EXP-06): who bills this stream's meter, as far as the product has been
          told. Borrowed from the census row this panel already sits in - a second read of the same
          fact would be a second answer. Vendor and supply account exist (ADR-0062); an item and a
          contract do not, and the sentence says so rather than implying a stream has none. */}
      <p className={own.formNote} data-testid="source-connections">
        <strong>Connections.</strong>{" "}
        {supply.state === "recorded"
          ? `Billed by ${supply.vendor_name ?? "a vendor"} under account ${supply.account_ref ?? "—"}. `
          : supply.state === "not_recorded"
            ? "Who bills this meter is not recorded. "
            : "Not tied to a meter, so no supply account applies. "}
        Items and contracts are not modelled yet, so none is listed here.
      </p>
      {data.activities.length > 0 ? (
        <ul className={own.linkList}>
          {data.activities.map((activity) => (
            <li key={activity.activity_id} className={own.linkItem}>
              <Link
                to="/records/$recordId"
                params={{ recordId: activity.activity_id }}
                search={keepSearch}
                className={own.link}
              >
                {shortDate(activity.period_start)} – {shortDate(activity.period_end)}
              </Link>{" "}
              {/* The same record, landing on its Workflow tab: a reading's approvals and flags
                  are the next thing a reader checking a submitted month wants. */}
              <RecordLink
                recordId={activity.activity_id}
                kind="activity_record"
                subject={`the ${statedQuantity(activity.quantity)} ${unitLabel(activity.unit)} reading`}
                tab="workflow"
              />{" "}
              <span className={own.rowMeta}>
                {statedQuantity(activity.quantity)} {unitLabel(activity.unit)}
              </span>
              {/* **Superseded is said, not implied by absence.** A link to a replaced figure
                  with nothing saying so is a number that looks live when it is not. */}
              {activity.is_effective ? null : (
                <span className={own.superseded}> · superseded by a correction</span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {data.documents.length > 0 ? (
        <ul className={own.linkList}>
          {data.documents.map((document) => (
            <li key={document.document_id} className={own.linkItem}>
              {/* **The document, not the library** (EXP-06's evidence destination). This linked to
                  `/evidence`, which is the review queue plus the most recent few recorded
                  documents — so a link naming `march-invoice.pdf` landed on a screen where that
                  document is usually **not listed at all**, and the reader was left to find it.
                  `/evidence/$documentId` is the page the receipt drawer and the register already
                  use for exactly this (EXP-19), and it opens a document in any state.

                  **A spreadsheet is not read, it is imported**, and it goes to the other screen.
                  `/evidence/$documentId` fetches an extraction; a file of rows has none, so the
                  link worked and the page it opened said the read had failed. The API carries the
                  sniffed content type for precisely this decision and **nothing was asking it** —
                  the field was there, under the name `kind`, unread, while the failure it was
                  added to prevent was live. */}
              {isTabularContentType(document.content_type) ? (
                <Link
                  to="/evidence/$documentId/import"
                  params={{ documentId: document.document_id }}
                  search={keepSearch}
                  className={own.link}
                >
                  {document.original_filename ?? "Spreadsheet"}
                </Link>
              ) : (
                <Link
                  to="/evidence/$documentId"
                  params={{ documentId: document.document_id }}
                  search={keepSearch}
                  className={own.link}
                >
                  {document.original_filename ?? "Document"}
                </Link>
              )}{" "}
              <span className={own.rowMeta}>
                {document.state.replace(/_/g, " ")}
                {/* One annual invoice behind twelve readings is one document, and the count is
                    what says so — listing it twelve times would read as twelve bills. */}
                {document.cites > 1 ? ` · cited by ${document.cites} readings` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
    {/* **Its own box, beside the readings and not inside them**: the readings are what arrived and
        this is what people said about what did not. They answer different questions, and the
        gate counts the evidence links inside the first. */}
    <NilTrail sourceId={sourceId} from={from} to={to} meId={meId} />
    </>
  );
}

/** Who said nil, when, who took it back, when, and why — **withdrawn declarations included**
 * (ADR-0064).
 *
 * An assurance provider's first question about a declared zero is who said so and why; the second
 * is whether anybody later said they were wrong. The trail answers both from the same rows, and a
 * withdrawn declaration is shown whole — *withdrawn* in words, because muted styling alone would
 * read as less important rather than as retracted.
 *
 * Renders nothing when the source has never been declared nil: an empty box headed "Declared nil"
 * would say a declaration was expected.
 */
function NilTrail({
  sourceId,
  from,
  to,
  meId,
}: {
  sourceId: string;
  from: string;
  to: string;
  meId: string | undefined;
}) {
  const { data, error, refetch } = useNilTrail(sourceId, from, to);
  const personName = useNameOf();
  if (error) {
    return <ReadFailed of="This source's declarations" error={error} onRetry={() => void refetch()} />;
  }
  if (!data || data.entries.length === 0) return null;

  /** *You*, else what the server says this reader may be told (ADR-0218): a name, or for an Auditor
   * the role. An id is never printed (rule 4). `null` is a person since erased. */
  const who = (id: string | null) =>
    id === null ? "someone since removed" : id === meId ? "you" : (personName(id) ?? "a colleague");

  return (
    <div className={own.links} data-testid="nil-trail">
      <p className={own.formHead}>Confirmed none</p>
      <ul className={own.trail}>
        {data.entries.map((entry) => (
          <li
            key={entry.declaration_id}
            className={`${own.trailItem} ${entry.standing ? "" : own.trailWithdrawn}`}
            data-standing={entry.standing ? "yes" : "no"}
          >
            <strong>
              {shortDate(entry.period_start)} – {shortDate(entry.period_end)}
            </strong>{" "}
            · {entry.standing ? "standing" : "withdrawn"}
            <br />
            Declared {shortDate(entry.declared_at)} by {who(entry.declared_by)}: &ldquo;
            {entry.reason}&rdquo;
            {!entry.standing && entry.withdrawal_reason ? (
              <>
                <br />
                Withdrawn {shortDate(entry.withdrawn_at ?? null)} by {who(entry.withdrawn_by ?? null)}:
                &ldquo;{entry.withdrawal_reason}&rdquo;
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SourcesScreen() {
  return (
    <RequireSession>
      <RequirePeriod title="Coverage" crumb="what we expect">
        {(screen, current) => <SourcesScreenBody screen={screen} current={current} />}
      </RequirePeriod>
    </RequireSession>
  );
}
